import React, { createContext, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { MODULE_DEFINITIONS, getModuleForPermission } from './modules';
import { HOTEL_SCOPED_PERMISSION_CODES, PERMISSION_DENIED_MESSAGE } from './permissions';

const PermissionContext = createContext(null);

export const getDjangoRbacContext = () => window.DJANGO_CONTEXT || {};

export const getCurrentPermissionState = () => {
  const context = getDjangoRbacContext();
  return {
    userPermissions: Array.isArray(context.user_permissions) ? context.user_permissions : [],
    hotelFeatures: Array.isArray(context.selected_hotel_enabled_features)
      ? context.selected_hotel_enabled_features
      : [],
    allowHotelsWithoutFeaturePackages: context.allow_hotels_without_feature_packages !== false,
  };
};

export const hasScopedUserPermissions = (permissionCodes = []) => {
  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return false;
  return permissionCodes.includes('*') || permissionCodes.some((code) => HOTEL_SCOPED_PERMISSION_CODES.has(code));
};

export const isHotelFeatureAssigned = (permissionCode, state = getCurrentPermissionState()) => {
  if (!HOTEL_SCOPED_PERMISSION_CODES.has(permissionCode)) return true;
  const enabledCodes = Array.isArray(state.hotelFeatures) ? state.hotelFeatures : [];
  if (enabledCodes.length === 0 && state.allowHotelsWithoutFeaturePackages) return true;
  return enabledCodes.includes(permissionCode);
};

export const isModuleAssigned = (moduleCode, state = getCurrentPermissionState()) => {
  const moduleDefinition = MODULE_DEFINITIONS[moduleCode];
  if (!moduleDefinition) return true;
  if (moduleDefinition.independent) return true;

  const dependenciesSatisfied = (moduleDefinition.dependsOn || []).every((dependencyCode) =>
    isModuleAssigned(dependencyCode, state)
  );
  if (!dependenciesSatisfied) return false;

  return moduleDefinition.featurePermissions.some((permissionCode) =>
    isHotelFeatureAssigned(permissionCode, state)
  );
};

export const can = (permissionCode, state = getCurrentPermissionState()) => {
  if (!isHotelFeatureAssigned(permissionCode, state)) return false;
  const permissionCodes = Array.isArray(state.userPermissions) ? state.userPermissions : [];
  if (permissionCodes.includes('*')) return true;
  if (!hasScopedUserPermissions(permissionCodes)) return true;
  return permissionCodes.includes(permissionCode);
};

export const isPermissionRelevantForHotel = (permissionCode, state = getCurrentPermissionState()) => {
  const moduleCode = getModuleForPermission(permissionCode);
  if (!moduleCode) return true;
  return isModuleAssigned(moduleCode, state);
};

export const PermissionProvider = ({ children }) => {
  const value = useMemo(() => {
    const state = getCurrentPermissionState();
    return {
      ...state,
      can: (permissionCode) => can(permissionCode, state),
      isHotelFeatureAssigned: (permissionCode) => isHotelFeatureAssigned(permissionCode, state),
      isModuleAssigned: (moduleCode) => isModuleAssigned(moduleCode, state),
      isPermissionRelevantForHotel: (permissionCode) => isPermissionRelevantForHotel(permissionCode, state),
    };
  }, []);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissionContext = () => useContext(PermissionContext) || {
  ...getCurrentPermissionState(),
  can,
  isHotelFeatureAssigned,
  isModuleAssigned,
  isPermissionRelevantForHotel,
};

export const PermissionGuard = ({ permissionCode, children, fallbackPath }) => {
  const permissions = usePermissionContext();
  if (permissions.can(permissionCode)) return children;
  return <Navigate to={fallbackPath || '/profile/'} replace />;
};

export { PERMISSION_DENIED_MESSAGE };
